import { Grid } from '@mui/material'
import { MainCard } from 'Components/Cards'
import { ConfirmationDialog } from 'Components/Dialog'
import { SectionLoader } from 'Components/Section'
import { KoreanLanguagePath } from 'Constants/Navigation'
import { URL_KEYS } from 'Constants/Url'
import { IKoreanSchoolContent, normalizeKoreanSchoolContentsList } from 'Entities/koreanSchoolContent'
import { useApiQuery } from 'Hooks/App/useApiQuery'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import httpClient from 'Service'
import { APP } from 'Constants/App'
import { useKoreanLanguageConfig } from 'Views/KoreanLanguage/Hooks'

export const KoreanLanguageContentList = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { subPath, schoolContentQueryType } = useKoreanLanguageConfig()
  const [removeId, setRemoveId] = useState<number | null>(null)

  const { data, isLoading, isFetching } = useApiQuery<unknown>({
    key: 'koreanSchoolContents',
    url: '/api/org/korean-school-contents',
    params: {
      type: schoolContentQueryType,
    },
  })

  const items = useMemo(() => normalizeKoreanSchoolContentsList(data), [data])

  const deleteMutation = useMutation({
    mutationFn: (id: number) => httpClient.delete(`/api/account/korean-school-contents/${id}`),
    onSuccess: () => {
      toast.success(APP.DATA_SUCCESS_REMOVED)
      queryClient.invalidateQueries({ queryKey: ['koreanSchoolContents'] })
      queryClient.invalidateQueries({
        predicate: q =>
          Array.isArray(q.queryKey) && String(q.queryKey[0] ?? '').startsWith('koreanSchoolContent-one'),
      })
    },
    onError: () => {
      toast.error('Не удалось удалить запись.')
    },
  })

  const [searchParams] = useSearchParams()
  const lang = searchParams.get(URL_KEYS.LANG)

  const getTitle = useCallback(
    (item: IKoreanSchoolContent) => {
      switch (lang) {
        case 'Ru':
          return item.titleRu
        case 'Uz':
          return item.titleUz
        case 'Kr':
          return item.titleKr
        default:
          return item.titleRu
      }
    },
    [lang],
  )

  const onEdit = useCallback(
    (id: number) => {
      navigate(`/${KoreanLanguagePath.main}/${subPath}/edit/${id}`)
    },
    [navigate, subPath],
  )

  const onRemoveConfirm = useCallback(() => {
    if (removeId != null) {
      deleteMutation.mutate(removeId, {
        onSettled: () => setRemoveId(null),
      })
    }
  }, [deleteMutation, removeId])

  return (
    <>
      <ConfirmationDialog
        open={removeId !== null}
        onAccept={onRemoveConfirm}
        onClose={() => setRemoveId(null)}
        text={
          <>
            В этом объекте есть связанные файлы. <br /> После удаления они тоже уничтожается!
          </>
        }
      />
      <SectionLoader isLoading={isLoading} isFetching={isFetching}>
        {!isLoading && (
          <>
            <Grid container rowSpacing={2}>
              {items.map(item => (
                <Grid item key={item.id} xs={12}>
                  <MainCard
                    onEdit={onEdit}
                    onRemove={setRemoveId}
                    id={item.id}
                    text={getTitle(item)}
                    date={
                      item.lastModifiedDate || item.createdDate
                        ? moment(item.lastModifiedDate || item.createdDate).format('DD.MM.YYYY HH:mm')
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </SectionLoader>
    </>
  )
}
